import { useEffect, useState } from 'react';
import { UserProfile } from '../types';
import { Session } from '@supabase/supabase-js';
import { initFeaturebase, cleanupFeaturebase, isFeaturebaseInitialized } from '../services/featurebaseService';

/**
 * Hook React pour gérer l'initialisation de Featurebase
 * 
 * @param userProfile - Profil utilisateur (null si non connecté)
 * @param session - Session Supabase (null si non connecté)
 * @param disabled - Désactiver Featurebase (ex: pour la route iframe/estimation)
 * @returns État d'initialisation de Featurebase
 */
export const useFeaturebase = (userProfile: UserProfile | null, session: Session | null, disabled: boolean = false) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    console.log('[Featurebase Hook] Effect triggered', { 
      hasUserProfile: !!userProfile, 
      hasSession: !!session,
      userProfileId: userProfile?.id,
      sessionUserId: session?.user?.id,
      disabled
    });

    // Ne pas initialiser si désactivé (ex: route iframe/estimation)
    if (disabled) {
      console.log('[Featurebase Hook] Skipping - disabled');
      // Nettoyer si on était initialisé avant
      if (isInitialized) {
        cleanupFeaturebase();
        setIsInitialized(false);
      }
      return;
    }

    // Ne pas initialiser si l'utilisateur n'est pas connecté
    if (!userProfile || !session) {
      console.log('[Featurebase Hook] Skipping - user not authenticated');
      // Nettoyer si on était initialisé avant
      if (isInitialized) {
        cleanupFeaturebase();
        setIsInitialized(false);
      }
      return;
    }

    // Ne pas réinitialiser si déjà fait
    if (isFeaturebaseInitialized()) {
      console.log('[Featurebase Hook] Already initialized, skipping');
      setIsInitialized(true);
      return;
    }

    // Initialiser Featurebase
    const initialize = async () => {
      console.log('[Featurebase Hook] Starting initialization...');
      try {
        await initFeaturebase(userProfile, session);
        setIsInitialized(true);
        console.log('[Featurebase Hook] Initialization successful');
      } catch (error) {
        console.error('[Featurebase Hook] Failed to initialize Featurebase:', error);
        setIsInitialized(false);
      }
    };

    initialize();

    // Cleanup lors du unmount ou changement d'utilisateur
    return () => {
      // Note: On ne nettoie pas complètement car le widget peut être réutilisé
      // Le cleanup sera fait lors de la déconnexion complète
    };
  }, [userProfile?.id, session?.user?.id, disabled]); // Dépendances: userProfile.id, session.user.id et disabled

  return { isInitialized };
};
